import {MutableRefObject, useCallback, useEffect, useMemo, useRef} from 'react';
import {UseQueryResult} from 'react-query';
import {FeatureFilterType, LayerId} from '@mc/react-mapio';
import {LayerVO} from '../../api';
import {useMapioContext} from '../../mapioContext';
import {useLayersConfig} from '../../hooks/useLayersConfig';

/** Debounce pro re-aktivaci extentu při pohybu mapou – sloučí rychlé pohyby (např. hover přes víc řádků) do 1 requestu. */
const EXTENT_REFRESH_DEBOUNCE_MS = 400;

/**
 * Aktivace extent filtru pro objekty ve výřezu – celá uvnitř SearchPanelu (volá `setLayerFeaturesFilter` přímo).
 * - při otevření panelu / změně vrstev aktivuje SHOW_EXTENT pro prohledávatelné aktivní vrstvy (okamžitě),
 * - svou re-aktivační funkci zaregistruje do předaného `extentRefreshRef`; MapPage ji volá při pohybu mapou
 *   (mapové eventy chodí jen do MapPage). Re-aktivace je debouncovaná, takže rychlé pohyby = 1 dotaz.
 * Po odmontování (zavření panelu) registraci i debounce uklidí.
 */
export const useSearchPanelExtent = (extentRefreshRef: MutableRefObject<(() => void) | null>, enabled: boolean): void => {
    const mapioContext = useMapioContext();
    const {mapRef, activeLayers} = mapioContext.value;
    const layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}> = useLayersConfig();
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const searchableActiveLayerIds = useMemo<LayerId[]>(() => {
        if (!layersConfigQuery.data) return [];
        return Object.values(layersConfigQuery.data)
            .filter((l: LayerVO) => l.searchable && activeLayers.includes(l.id as LayerId))
            .map((l: LayerVO) => l.id as LayerId);
    }, [layersConfigQuery.data, activeLayers]);
    const searchableActiveLayerIdsKey = useMemo(() => searchableActiveLayerIds.join(','), [searchableActiveLayerIds]);

    /** Okamžitá aktivace extent filtru (otevření panelu / změna vrstev). Voláme i s prázdným polem (žádná
     * prohledávatelná vrstva aktivní) – jinak by engine nikdy nepřepočítal EXTENT_FEATURES a seznam by zůstal
     * viset na starých objektech z doby, kdy ještě nějaká vrstva aktivní byla (např. po úplném vyčištění filtru). */
    const applyExtent = useCallback(() => {
        mapRef?.current?.setLayerFeaturesFilter(FeatureFilterType.SHOW_EXTENT, searchableActiveLayerIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchableActiveLayerIdsKey, mapRef]);

    /** Debouncovaná re-aktivace – volá ji MapPage při každém pohybu mapou; rychlé pohyby se sloučí. */
    const refresh = useCallback(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => applyExtent(), EXTENT_REFRESH_DEBOUNCE_MS);
    }, [applyExtent]);

    useEffect(() => {
        // Jen když je panel hledání aktivní (viditelný). Při skrytí (detail / jiný obsah) extent neaktivujeme.
        if (!enabled) return;
        // Registrace debouncované re-aktivace pro MapPage + iniciální (okamžitá) aktivace při otevření panelu.
        extentRefreshRef.current = refresh;
        applyExtent();
        // Debouncovaný retry – pokryje případ, kdy prvky vrstev ještě nebyly načtené při okamžité aktivaci
        // (jinak by seznam po prvním načtení zůstal prázdný do prvního pohybu mapou).
        refresh();
        return () => {
            if (extentRefreshRef.current === refresh) extentRefreshRef.current = null;
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [enabled, refresh, applyExtent, extentRefreshRef]);
};
